import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HealthBadge from '../../components/Management/HealthBadge';
import { analyticsAPI } from '../../services/analytics';

const DemoBreakdown = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: res, isLoading, error } = useQuery(
    ['demoBreakdown', id],
    () => analyticsAPI.getDemoBreakdown(id),
    { enabled: Boolean(id) }
  );

  const data = res?.data?.data;
  const demo = data?.demo;
  const summary = data?.summary;
  const leads = data?.leads || [];

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management')} sx={{ mb: 2 }}>
        Back to management
      </Button>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error.response?.data?.message || error.message}</Alert>}

      {!isLoading && !error && demo && (
        <>
          <Typography variant="h5" component="h1" gutterBottom>
            Demo — {demo.teacherName} — {demo.scheduledDate}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {demo.demoType === 'group' ? 'Group demo' : 'Demo'} · Status: {demo.status}
          </Typography>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {summary.invited} Invited · {summary.attended} Attended · {summary.enrolled} Enrolled
              </Typography>
              {summary.attendanceEstimated && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Attendance estimated until per-lead attendance is recorded at demo close.
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    Attendance: {summary.attendancePct}%
                  </Typography>
                  <HealthBadge
                    healthStatus={summary.attendanceHealth?.healthStatus}
                    healthLabel={summary.attendanceHealth?.healthLabel}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    Conversion: {summary.conversionPct}%
                  </Typography>
                  <HealthBadge
                    healthStatus={summary.conversionHealth?.healthStatus}
                    healthLabel={summary.conversionHealth?.healthLabel}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lead</TableCell>
                  <TableCell>Attended</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Days waiting</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.leadId}>
                    <TableCell>
                      {lead.name}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {lead.displayId}
                      </Typography>
                    </TableCell>
                    <TableCell>{lead.attended ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{lead.status}{lead.subStatus ? ` — ${lead.subStatus}` : ''}</TableCell>
                    <TableCell>{lead.daysWaiting != null ? lead.daysWaiting : '—'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => navigate(`/leads?search=${encodeURIComponent(lead.displayId || '')}`)}
                      >
                        Open lead
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default DemoBreakdown;
